import { createElement, memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Download, Image, Link2, Play, Reply, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  attachmentIcon,
  attachmentKind,
  extractUrls,
  formatBytes,
  groupAttachments,
  type AttachmentKind,
} from '../lib/attachments';
import { findChatBridge } from '../lib/chatBridge';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  EmptyState,
  MediaViewer,
  Scroller,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  TabsCount,
  Tooltip,
  type MediaViewerItem,
} from '@/shared/ui';
import { ChatIcon } from '@/shared/config';
import { copyToClipboard } from '@/shared/lib/clipboard';
import { resolveApiUrl } from '@/shared/lib/config';
import { cn } from '@/shared/lib/cn';
import { AttachmentIcon } from '@/shared/config';
import type { MessageAttachment, TicketLegacyFile, TicketMessage } from '@/entities/ticket';
import { sameOriginPath } from '@/shared/lib/safe-path';
import { onExternalLinkClick } from '@/shared/lib/external-link';
import styles from './AttachmentsPanel.module.css';

const rowIcon = (kind: AttachmentKind) => createElement(attachmentIcon(kind), { size: 18 });

interface Origin {
  messageId: string;
  ticketId: string;
}

export type PanelAttachment = MessageAttachment & Origin;
export type PanelLink = Origin & { url: string };

export interface TicketMedia {
  media: PanelAttachment[];
  rows: PanelAttachment[];
  links: PanelLink[];
  galleryItems: MediaViewerItem[];
}

export function useTicketMedia(messages: TicketMessage[]): TicketMedia {
  const attachments = useMemo(
    () =>
      messages.flatMap((m) =>
        (m.attachments ?? []).map((a) => ({ ...a, messageId: m.id, ticketId: m.ticketId })),
      ),
    [messages],
  );
  const { media, audio, documents, files } = useMemo(
    () => groupAttachments(attachments),
    [attachments],
  );
  const rows = useMemo(() => [...audio, ...documents, ...files], [audio, documents, files]);

  const links = useMemo(() => {
    const seen = new Set<string>();
    const out: PanelLink[] = [];
    for (const m of messages) {
      for (const u of extractUrls(m.body)) {
        if (!seen.has(u)) {
          seen.add(u);
          out.push({ url: u, messageId: m.id, ticketId: m.ticketId });
        }
      }
    }
    return out;
  }, [messages]);

  const galleryItems = useMemo<MediaViewerItem[]>(
    () =>
      media.map((a) => ({
        url: resolveApiUrl(a.url),
        fileName: a.fileName,
        type: attachmentKind(a.mimeType, a.fileName) === 'video' ? ('video' as const) : ('image' as const),
      })),
    [media],
  );

  return { media, rows, links, galleryItems };
}

function linkLabel(url: string): string {
  try {
    return decodeURI(url);
  } catch {
    return url;
  }
}

function ChatItems({ origin, attachmentId }: { origin: Origin; attachmentId?: string }) {
  const chat = findChatBridge(origin.ticketId, origin.messageId);
  if (!chat) return null;
  const canDelete = attachmentId !== undefined && chat.canDelete(origin.messageId);
  return (
    <>
      <ContextMenuSeparator />
      <ContextMenuItem onClick={() => chat.jumpTo(origin.messageId)}>
        <ChatIcon size={14} aria-hidden /> Перейти к сообщению
      </ContextMenuItem>
      <ContextMenuItem onClick={() => chat.reply(origin.messageId, attachmentId)}>
        <Reply size={14} aria-hidden /> Ответить на сообщение
      </ContextMenuItem>
      {canDelete && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem
            variant="danger"
            onClick={() => chat.requestDeleteAttachment(origin.messageId, attachmentId)}
          >
            <Trash2 size={14} aria-hidden /> Удалить файл
          </ContextMenuItem>
        </>
      )}
    </>
  );
}

function download(a: MessageAttachment) {
  const link = document.createElement('a');
  link.href = resolveApiUrl(a.url);
  link.download = a.fileName;
  link.rel = 'noreferrer';
  link.click();
}

function ItemMenu({
  origin,
  attachmentId,
  tooltip,
  own,
  children,
}: {
  origin: Origin;
  attachmentId?: string;
  tooltip?: string;
  own: React.ReactNode;
  children: React.ReactElement;
}) {
  const trigger = <ContextMenuTrigger render={children} />;
  return (
    <ContextMenu>
      {tooltip ? <Tooltip content={tooltip}>{trigger}</Tooltip> : trigger}
      <ContextMenuContent>
        {own}
        <ChatItems origin={origin} attachmentId={attachmentId} />
      </ContextMenuContent>
    </ContextMenu>
  );
}

export const MediaTab: React.FC<{ media: PanelAttachment[]; galleryItems: MediaViewerItem[] }> = ({
  media,
  galleryItems,
}) => {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  if (media.length === 0) {
    return <EmptyState compact Icon={Image} title="Медиа нет" hint="Фото и видео из заявки и чата появятся здесь" />;
  }
  return (
    <>
      <Scroller className={styles.gallery} viewportClassName={styles.galleryViewport}>
        {media.map((a, i) => {
          const kind = attachmentKind(a.mimeType, a.fileName);
          const thumbUrl = a.thumbUrl
            ? resolveApiUrl(a.thumbUrl)
            : kind === 'video'
              ? null
              : resolveApiUrl(a.url);
          return (
            <ItemMenu
              key={a.id}
              origin={a}
              attachmentId={a.id}
              own={
                <ContextMenuItem onClick={() => download(a)}>
                  <Download size={14} aria-hidden /> Скачать
                </ContextMenuItem>
              }
            >
              <button
                type="button"
                className={styles.tile}
                title={a.fileName}
                onClick={() => setViewerIndex(i)}
              >
                {thumbUrl ? (
                  <img src={thumbUrl} alt={a.fileName} loading="lazy" decoding="async" />
                ) : (
                  <span className={styles.tilePlaceholder} aria-hidden />
                )}
                {kind === 'video' && (
                  <span className={styles.playOverlay} aria-hidden>
                    <Play size={18} fill="currentColor" />
                  </span>
                )}
                {kind === 'gif' && <span className={styles.badge}>GIF</span>}
              </button>
            </ItemMenu>
          );
        })}
      </Scroller>

      {viewerIndex !== null && (
        <MediaViewer
          items={galleryItems}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </>
  );
};

export const FilesTab: React.FC<{ rows: PanelAttachment[] }> = ({ rows }) =>
  rows.length === 0 ? (
    <EmptyState compact Icon={AttachmentIcon} title="Файлов нет" hint="Документы из заявки и чата появятся здесь" />
  ) : (
  <Scroller className={styles.fileList} viewportClassName={styles.fileListViewport}>
    {rows.map((a) => (
      <ItemMenu
        key={a.id}
        origin={a}
        attachmentId={a.id}
        tooltip="Скачать"
        own={
          <ContextMenuItem onClick={() => download(a)}>
            <Download size={14} aria-hidden /> Скачать
          </ContextMenuItem>
        }
      >
        <a
          className={styles.fileRow}
          href={resolveApiUrl(a.url)}
          target="_blank"
          rel="noreferrer"
          download={a.fileName}
        >
          <span className={styles.fileIcon}>{rowIcon(attachmentKind(a.mimeType, a.fileName))}</span>
          <span className={styles.fileInfo}>
            <span className={styles.fileName}>{a.fileName}</span>
            <span className={styles.fileSize}>{formatBytes(a.sizeBytes)}</span>
          </span>
        </a>
      </ItemMenu>
    ))}
  </Scroller>
  );

export const LegacyFilesTab: React.FC<{ files: TicketLegacyFile[] }> = ({ files }) => (
  <Scroller className={styles.fileList} viewportClassName={styles.fileListViewport}>
    {files.map((f) => (
      <div key={f.uid || f.path || f.name} className={cn(styles.fileRow, styles.legacyRow)} title={f.path}>
        <span className={styles.fileIcon}>{rowIcon(attachmentKind('', f.name))}</span>
        <span className={styles.fileInfo}>
          <span className={styles.fileName}>{f.name}</span>
          <span className={styles.fileSize}>{f.description ?? 'Файл ещё в Итилиум'}</span>
        </span>
      </div>
    ))}
  </Scroller>
);

export const LinksTab: React.FC<{ links: PanelLink[] }> = ({ links }) =>
  links.length === 0 ? (
    <EmptyState compact Icon={Link2} title="Ссылок нет" hint="Ссылки из описания и чата появятся здесь" />
  ) : (
  <Scroller className={styles.fileList} viewportClassName={styles.fileListViewport}>
    {links.map((link) => {
      const { url } = link;
      const internal = sameOriginPath(url);
      const body = (
        <>
          <span className={styles.fileIcon}>
            <Link2 size={18} />
          </span>
          <span className={styles.linkText}>{linkLabel(url)}</span>
        </>
      );
      const copy = (
        <ContextMenuItem
          onClick={() =>
            void copyToClipboard(url).then((ok) =>
              ok ? toast.success('Ссылка скопирована') : toast.error('Браузер не дал доступ к буферу обмена'),
            )
          }
        >
          <Copy size={14} aria-hidden /> Копировать ссылку
        </ContextMenuItem>
      );
      return (
        <ItemMenu key={url} origin={link} own={copy}>
          {internal ? (
            <Link className={styles.linkRow} to={internal} title={url}>
              {body}
            </Link>
          ) : (
            <a
              className={styles.linkRow}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              title={url}
              onClick={onExternalLinkClick(url)}
            >
              {body}
            </a>
          )}
        </ItemMenu>
      );
    })}
  </Scroller>
  );

function AttachmentsPanelBase({ messages }: { messages: TicketMessage[] }) {
  const { media, rows, links, galleryItems } = useTicketMedia(messages);

  const tabs = [
    media.length > 0 && { value: 'media', label: 'Медиа', count: media.length },
    links.length > 0 && { value: 'links', label: 'Ссылки', count: links.length },
    rows.length > 0 && { value: 'files', label: 'Файлы', count: rows.length },
  ].filter(Boolean) as Array<{ value: string; label: string; count: number }>;

  if (tabs.length === 0) {
    return <p className={styles.empty}>Вложений нет.</p>;
  }

  return (
    <div className={styles.root}>
      <Tabs defaultValue={tabs[0].value}>
        <TabsList className={styles.tabsList}>
          {tabs.map((t) => (
            <TabsTab key={t.value} value={t.value}>
              {t.label}
              <TabsCount value={t.count} />
            </TabsTab>
          ))}
        </TabsList>

        {media.length > 0 && (
          <TabsPanel value="media">
            <MediaTab media={media} galleryItems={galleryItems} />
          </TabsPanel>
        )}
        {links.length > 0 && (
          <TabsPanel value="links">
            <LinksTab links={links} />
          </TabsPanel>
        )}
        {rows.length > 0 && (
          <TabsPanel value="files">
            <FilesTab rows={rows} />
          </TabsPanel>
        )}
      </Tabs>
    </div>
  );
}

export const AttachmentsPanel = memo(AttachmentsPanelBase);
